import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiKeysApi } from "@/lib/account";

/**
 * Admin section: list all customer API keys, issue on behalf of a user,
 * revoke any key. Shows the plaintext key ONCE on issuance.
 */
export default function AdminApiKeysSection() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [issueForm, setIssueForm] = useState({ user_id: "", name: "" });
  const [issuing, setIssuing] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = (p = page, s = q) =>
    apiKeysApi
      .adminList({ page: p, page_size: 25, q: s })
      .then(setData)
      .catch((e) => toast.error(e.message || "Failed to load keys"));

  useEffect(() => {
    load(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onIssue = async (e) => {
    e.preventDefault();
    if (!issueForm.user_id.trim() || !issueForm.name.trim() || issuing) return;
    setIssuing(true);
    try {
      const r = await apiKeysApi.adminIssue({
        user_id: issueForm.user_id.trim(),
        name: issueForm.name.trim(),
      });
      setRevealed(r);
      setIssueForm({ user_id: "", name: "" });
      await load(1, q);
      setPage(1);
    } catch (err) {
      toast.error(err.message || "Failed to issue key");
    } finally {
      setIssuing(false);
    }
  };

  const onRevoke = async (id) => {
    if (!confirm("Revoke this key? The customer using it will be cut off immediately.")) return;
    setBusyId(id);
    try {
      await apiKeysApi.adminRevoke(id);
      await load(page, q);
    } catch (err) {
      toast.error(err.message || "Failed to revoke");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <p className="font-sans text-sm text-[var(--text-muted)] mb-5 leading-relaxed">
        Issue keys on behalf of users, audit usage, revoke compromised keys.
        Customer-facing self-service lives at <code className="font-mono-ui text-[12px]">/account/api-keys</code>.
      </p>

      {revealed && (
        <div
          data-testid="admin-key-reveal"
          className="border border-[var(--accent)] bg-[var(--bg-elevated)] p-5 mb-6"
        >
          <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--accent)] mb-3">
            New key for {revealed.user_email} — copy now
          </div>
          <code
            data-testid="admin-revealed-key"
            className="font-mono-ui text-[12px] text-[var(--text)] break-all block bg-[var(--bg)] p-3 border border-[var(--border)]"
          >
            {revealed.key}
          </code>
          <button
            onClick={() => setRevealed(null)}
            data-testid="admin-key-reveal-dismiss"
            className="mt-3 font-mono-ui text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Issue form */}
      <form onSubmit={onIssue} className="border border-[var(--border)] p-5 mb-6 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div>
          <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] block mb-2">
            User id
          </label>
          <input
            type="text"
            value={issueForm.user_id}
            onChange={(e) => setIssueForm({ ...issueForm, user_id: e.target.value })}
            placeholder="ObjectId"
            data-testid="admin-issue-user-id"
            className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-mono-ui text-sm text-[var(--text)] placeholder:text-[var(--text-dim)] focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
        <div>
          <label className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)] block mb-2">
            Key name
          </label>
          <input
            type="text"
            value={issueForm.name}
            onChange={(e) => setIssueForm({ ...issueForm, name: e.target.value })}
            placeholder="e.g. partner integration"
            data-testid="admin-issue-name"
            maxLength={80}
            className="w-full bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-ui text-sm text-[var(--text)] placeholder:text-[var(--text-dim)] focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
        <button
          type="submit"
          disabled={issuing || !issueForm.user_id.trim() || !issueForm.name.trim()}
          data-testid="admin-issue-submit"
          className="font-mono-ui text-[10px] tracking-[0.28em] uppercase px-4 py-2 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {issuing ? "Issuing…" : "Issue key"}
        </button>
      </form>

      {/* Search */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          load(1, q);
        }}
        className="flex gap-2 mb-4"
      >
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="filter by email…"
          data-testid="admin-keys-search"
          className="flex-1 bg-[var(--bg)] border border-[var(--border)] px-3 py-2 font-ui text-sm text-[var(--text)] placeholder:text-[var(--text-dim)] focus:outline-none focus:border-[var(--accent)]"
        />
        <button
          type="submit"
          data-testid="admin-keys-search-submit"
          className="font-mono-ui text-[10px] tracking-[0.28em] uppercase px-4 py-2 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
        >
          Search
        </button>
      </form>

      {/* Table */}
      <div className="space-y-2">
        {data?.keys.map((k) => (
          <div
            key={k.id}
            data-testid={`admin-key-row-${k.id}`}
            className="border border-[var(--border)] px-4 py-3 flex flex-wrap items-center gap-3 text-sm"
          >
            <div className="flex-1 min-w-0">
              <div className="font-ui text-[var(--text)] truncate">
                {k.name}{" "}
                <span className="font-mono-ui text-[9px] tracking-[0.18em] text-[var(--text-dim)] ml-2">
                  {k.key_prefix}…
                </span>
              </div>
              <div className="font-mono-ui text-[10px] tracking-[0.18em] text-[var(--text-dim)] mt-1 truncate">
                {k.user_email} · {k.requests_per_minute} req/min
                {" · "}
                {k.monthly_token_cap === null
                  ? "unlimited"
                  : `${k.tokens_used_this_month} / ${k.monthly_token_cap}`}
                {k.last_used_at && ` · last ${k.last_used_at.slice(0, 10)}`}
              </div>
            </div>
            <button
              onClick={() => onRevoke(k.id)}
              disabled={busyId === k.id}
              data-testid={`admin-key-revoke-${k.id}`}
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase px-3 py-1.5 border border-[var(--border)] hover:border-red-500 hover:text-red-400 disabled:opacity-40 transition-colors"
            >
              Revoke
            </button>
          </div>
        ))}
        {data && data.keys.length === 0 && (
          <p className="font-sans text-sm text-[var(--text-dim)]" data-testid="admin-keys-empty">
            No keys yet.
          </p>
        )}
      </div>

      {data && data.total > data.page_size && (
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={() => { const p = Math.max(1, page - 1); setPage(p); load(p, q); }}
            disabled={page <= 1}
            data-testid="admin-keys-prev"
            className="font-mono-ui text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-[var(--border)] disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="font-mono-ui text-[10px] tracking-[0.22em] text-[var(--text-muted)]">
            page {page} · {data.total} total
          </span>
          <button
            onClick={() => { const p = page + 1; setPage(p); load(p, q); }}
            disabled={page * data.page_size >= data.total}
            data-testid="admin-keys-next"
            className="font-mono-ui text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border border-[var(--border)] disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
